// FieldExtension.ts – drag‑and‑drop fixado (move interno, drop externo) para TipTap 2.12
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { FieldComponent } from './FieldComponent';
import { Plugin } from 'prosemirror-state';

// Tipagens extras ----------------------------------------------------------
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        field: {
            insertField: (attrs: {
                title: string;
                id_field: string;
                name: string;
                type: string;
                position?: string;
                id?: string;
            }) => ReturnType;
        };
    }
}

export interface FieldOptions {
    HTMLAttributes: Record<string, any>;
}

export const Field = Node.create<FieldOptions>({
    name: 'field',
    group: 'inline',
    inline: true,
    selectable: false,
    draggable: false,
    atom: true,

    addAttributes() {
        return {
            title: { default: '' },
            id_field: { default: '' },
            name: { default: '' },
            type: { default: 'field' },
            position: { default: '0' },
            id: {
                default: null,
                parseHTML: el => el.getAttribute('id'),
            },
            href: {
                default: null,
                parseHTML: el => el.getAttribute('href'),
            },
        } as const;
    },

    parseHTML() {
        return [{
            tag: 'span.field-dynamic-tag',
            getAttrs: (element) => {
                if (typeof element === 'string') return {};
                
                const el = element as HTMLElement;
                return {
                    title: el.getAttribute('field-title') || el.textContent || '',
                    id_field: el.getAttribute('field-id') || '',
                    name: el.getAttribute('field-name') || '',
                    type: el.getAttribute('field-type') || 'field',
                    position: el.getAttribute('field-position') || '0',
                    id: el.getAttribute('id') || null,
                    href: el.getAttribute('href') || null
                };
            }
        }];
    },

    renderHTML({ HTMLAttributes }) {
        const objectHash = require('object-hash');
        const id = HTMLAttributes.id || objectHash(`${Date.now()}${Math.random()}`);

        return [
            'span',
            mergeAttributes(
                {
                    class: 'field-dynamic-tag',
                    contenteditable: 'false',
                    draggable: 'true',
                    id,
                    href: id,
                    type: 'field',
                    'field-name': HTMLAttributes.name,
                    'field-title': HTMLAttributes.title,
                    'field-id': HTMLAttributes.id_field,
                    'field-type': HTMLAttributes.type,
                    'field-position': HTMLAttributes.position,
                },
                HTMLAttributes,
            ),
            HTMLAttributes.title,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(FieldComponent);
    },

    addCommands() {
        return {
            insertField:
                attrs =>
                    ({ chain, editor }) => {
                        const pos = editor.state.selection.anchor;
                        return chain()
                            .focus()
                            .insertContent({ type: this.name, attrs: { ...attrs, position: attrs.position ?? String(pos) } })
                            .run();
                    },
        };
    },

    // Plugin de DnD: só intercepta drops EXTERNOS (moved === false)
    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleDrop(view, event, slice, moved) {
                        const fieldId = event.dataTransfer?.getData('application/tiptap-field-id');
                        
                        // If this is not our field drag, let the default handler take over
                        if (!fieldId) {
                          return false;
                        }
                        
                        try {
                          // Get coordinates and position at drop location
                          const coordinates = view.posAtCoords({ 
                            left: event.clientX, 
                            top: event.clientY 
                          });
                          
                          if (!coordinates) {
                            return false;
                          }
                          
                          // Simpler approach: Instead of moving nodes, just delete the original and insert a copy
                          // Create a new field node with the same attributes
                          
                          // 1. Find the original node by ID
                          let originalAttrs = null;
                          let startPos = -1;
                          let endPos = -1;
                          
                          view.state.doc.descendants((node, pos) => {
                            if (node.type.name === 'field' && node.attrs.id === fieldId) {
                              originalAttrs = {...node.attrs};
                              startPos = pos;
                              endPos = pos + node.nodeSize;
                              return false; // stop traversal
                            }
                            return true;
                          });
                          
                          if (!originalAttrs || startPos === -1) {
                            console.error('Field node not found for moving');
                            return false;
                          }
                          
                          // 2. Create a transaction to delete the original node
                          let tr = view.state.tr.delete(startPos, endPos);
                          
                          // 3. Insert a new node with the same attributes at the drop position
                          const dropPos = coordinates.pos;
                          const nodeType = view.state.schema.nodes.field;
                          
                          tr = tr.insert(dropPos, nodeType.create(originalAttrs));
                          
                          // 4. Apply the transaction
                          view.dispatch(tr);
                          
                          // 5. Focus the editor
                          setTimeout(() => {
                            view.focus();
                          }, 0);
                          
                          return true; // we handled the drop
                        } catch (error) {
                          console.error('Error handling field drop:', error);
                          return false;
                        }
                      }
                }
            }),
        ];
    },
});

export default Field;
